
import Vue, { PropType } from 'vue';
import Close16 from '@carbon/icons-vue/es/close/16';
import { PREVIEW_PRODUCT_BOX } from '../../../constants';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { mapState } from 'pinia';
import { ProductVariantImage } from '@midocean/webapps-shared-domain';
import { getScaledProductImageDPI } from '@/utils/areaHelper';
import { mm2px } from '@/utils';
import ArrowDown16 from '@carbon/icons-vue/es/arrow--down/16';
import ArrowUp16 from '@carbon/icons-vue/es/arrow--up/16';

export default Vue.extend({
  name: 'ProductPreviewModal',

  components: {
    Close16,
    ArrowDown16,
    ArrowUp16,
  },

  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
    preSelectedPositionId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      PREVIEW_PRODUCT_BOX,
      positionId: this.preSelectedPositionId,
      displayImgsFrom: 0,
    };
  },

  computed: {
    ...mapState(useProductStore, ['printPositions', 'selectedVariant']),
    ...mapState(useShapeStore, ['positionShapes']),

    selectedPosition(): ProductVariantImage | undefined {
      return this.selectedVariant.images.find(
        (x) => x.printPositionID === this.positionId
      );
    },

    selectedVariantPositions(): {
      imageUrl: string;
      positionName: string;
      id: string;
    }[] {
      const productVariantImages = this.selectedVariant.images.filter((x) =>
        this.printPositions.some((p) => p.id === x.printPositionID)
      );

      return productVariantImages.map((x) => {
        const position = this.printPositions.find(
          (p) => p.id === x.printPositionID
        );

        return {
          id: x.printPositionID,
          imageUrl: x.imageThumbnailUrl,
          positionName: position?.name || '',
        };
      });
    },

    activePositionShapes(): {
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      pngFileUrl: string | null;
      rotation: number;
    }[] {
      const printPosition = this.printPositions.find(
        (x) => x.id === this.positionId
      );

      if (printPosition) {
        const selectedShapes = this.positionShapes(printPosition.id).filter(
          (x) => x.isInArea
        );

        const mappedPositions = selectedShapes.map((x) => {
          const scaledDPI = getScaledProductImageDPI(
            printPosition.originalImageWidthPx,
            printPosition.originalImageHeightPx,
            printPosition.originalImageDpi,
            PREVIEW_PRODUCT_BOX
          );

          return {
            id: x.id,
            x: mm2px(x.xOnProductMM, scaledDPI),
            y: mm2px(x.yOnProductMM, scaledDPI),
            width: mm2px(x.widthOnProductMM, scaledDPI),
            height: mm2px(x.heightOnProductMM, scaledDPI),
            pngFileUrl:
              x.kind === 'Logo'
                ? x.pngFileUrl
                : URL.createObjectURL(x.pngFile as Blob),
            rotation: x.rotation,
          };
        });

        return mappedPositions;
      }

      return [];
    },
  },

  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.$modal.show('product-preview-modal');
      } else {
        this.$modal.hide('product-preview-modal');
      }
    },
  },

  mounted() {
    if (this.isOpen) {
      this.$modal.show('product-preview-modal');
    }
  },
  methods: {
    scrollImageNavigation(start: number) {
      this.displayImgsFrom = start;
    },
    isImageHidden(index: number) {
      let start = this.displayImgsFrom;
      let end = this.displayImgsFrom + 3;
      let imagesToDisplay = Array.from(
        { length: end - start + 1 },
        (_, i) => start + i
      );

      return !imagesToDisplay.includes(index);
    },
  },
});
